/**
 * Frontend copy of the unified lead-import schema + auto-mapping rules — kept in
 * sync with packages/api/src/crm/leadSchema.ts and the wequote-lead-import
 * skill's convert_leads.py. Used by the mapping UI to auto-suggest field
 * mappings client-side (instant, no round-trip).
 */

export interface FieldGroup {
  name: string
  color: string
  fields: string[]
}
export const UNIFIED_GROUPS: FieldGroup[] = [
  { name: 'Source', color: '#1B3A52', fields: ['Opportunity', 'Lead Source', 'Captured Date'] },
  { name: 'Contact', color: '#2D6A8F', fields: ['Full Name', 'Job Title', 'Job Function', 'Email', 'Secondary Email', 'Phone', 'Mobile', 'LinkedIn URL'] },
  { name: 'Company', color: '#1B3A52', fields: ['Company Name', 'Website', 'Industry / Primary Business', 'Business Type / Channel', 'Markets Served', 'Company Size', 'Annual Revenue / AV Spend', 'Association / Membership'] },
  { name: 'Location', color: '#2D6A8F', fields: ['Address 1', 'Address 2', 'City', 'State / Region', 'Postcode', 'Country'] },
  { name: 'Free-form', color: '#1B3A52', fields: ['Notes'] },
]
export const UNIFIED_FIELDS: string[] = UNIFIED_GROUPS.flatMap((g) => g.fields)
export const REQUIRED_FIELDS = ['Email', 'Company Name']

// mapping sentinels
export const TO_NOTES = '__notes'
export const IGNORE = '__ignore'
export const FIRSTNAME = '__firstname'
export const LASTNAME = '__lastname'

const ALIASES: Record<string, string[]> = {
  'Job Title': ['job ?title', '^position$', 'contact job title', 'job title_'],
  'Job Function': ['job ?function', 'primary job function'],
  Email: ['^e-?mail$', '^email address$', '^email ?id$', '^email$'],
  'Secondary Email': ['secondary email', 'email ?2', 'alt.*email'],
  Phone: ['^phone', 'phone ?number', 'phone ?1$', 'telephone', '^tel$'],
  Mobile: ['mobile', '^cell', 'cell phone'],
  'LinkedIn URL': ['linkedin'],
  'Company Name': ['company ?name', '^company( ?1)?$', 'organi[sz]ation', '^org$'],
  Website: ['web ?site', '^url$', '^web$'],
  'Industry / Primary Business': ['industry', 'primary business', 'what is the primary business'],
  'Business Type / Channel': ['business type', '^channel$', 'primary business ?- ?channel'],
  'Markets Served': ['markets', 'which .*markets', 'which of these markets'],
  'Company Size': ['company size', 'employees', '^size$', 'no\\.? of employees'],
  'Annual Revenue / AV Spend': ['revenue', 'annual.*spend', 'spend.*annual', 'av spend', 'gross annual'],
  'Association / Membership': ['cedia member', 'member(ship)?$', 'association'],
  'Address 1': ['^address ?1?$', '^address$', '^street'],
  'Address 2': ['^address ?2$', '^address ?3$', 'suite', 'unit'],
  City: ['^city$', '^town$'],
  'State / Region': ['^state', 'province', '^county$', '^region$'],
  Postcode: ['post ?code', 'postal ?code', '^zip'],
  Country: ['^country$'],
  'Captured Date': ['scanned date', 'captured date', '^date$', 'scan date'],
  'Lead Source': ['lead source', '^source$', 'lead management response type'],
  Opportunity: ['opportunity', 'event name'],
  Notes: ['^notes?$', 'comments?'],
}
const FIRST_PATTERNS = ['first ?name', '^f ?name$', 'given name']
const LAST_PATTERNS = ['last ?name', '^l ?name$', 'surname', 'family name']

const norm = (h: unknown) => (h == null || h === '' ? '' : String(h).trim().toLowerCase().replace(/\s+/g, ' '))
const stripStar = (h: string) => norm(h).replace(/^★\s*/, '').replace(/\s*\*$/, '')
export const isFirstName = (h: string) => FIRST_PATTERNS.some((p) => new RegExp(p).test(norm(h)))
export const isLastName = (h: string) => LAST_PATTERNS.some((p) => new RegExp(p).test(norm(h)))

/** Exact unified-field name match (handles ★/'*' prefixes on the template). */
function exactField(header: string): string | null {
  const n = stripStar(header)
  return UNIFIED_FIELDS.find((f) => norm(f) === n) || null
}

/** Best-effort target field, honouring the skill's special rules (standalone
 * "title" = salutation → Notes; singular "market" = temperature → Notes). */
export function matchTarget(header: string): string | null {
  const n = norm(header)
  if (!n) return null
  if (n === 'title') return null
  if (n === 'market') return null
  for (const [tgt, pats] of Object.entries(ALIASES)) {
    if (pats.some((p) => new RegExp(p).test(n))) return tgt
  }
  return null
}

export type Mapping = Record<string, string> // source header -> field | sentinel

/** Auto-suggest a mapping for the given source headers. First/Last are detected
 * and marked to combine into Full Name; anything unmatched defaults to → Notes
 * (never discarded). */
export function autoMapHeaders(headers: string[]): Mapping {
  const mapping: Mapping = {}
  let firstUsed = false
  let lastUsed = false
  for (const h of headers) {
    const exact = exactField(h)
    if (exact) { mapping[h] = exact; continue }
    if (!firstUsed && isFirstName(h)) { mapping[h] = FIRSTNAME; firstUsed = true; continue }
    if (!lastUsed && isLastName(h)) { mapping[h] = LASTNAME; lastUsed = true; continue }
    const t = matchTarget(h)
    mapping[h] = t || TO_NOTES
  }
  return mapping
}

export interface ImportMetaLite {
  opportunity: string
  capturedDate: string
  leadSource: string
}
/** Build one unified 26-field record from a source row + the mapping + import
 * meta. First/Last combine into Full Name; multiple sources for one field are
 * joined with " / "; TO_NOTES columns and any explicit Notes are folded into
 * Notes as "Header: value" (nothing discarded). Mirrors convert_leads.py. */
export function buildUnifiedRecord(headers: string[], row: string[], mapping: Mapping, meta: ImportMetaLite): Record<string, string> {
  const rec: Record<string, string> = {}
  for (const f of UNIFIED_FIELDS) rec[f] = ''
  let first = ''
  let last = ''
  const notesExtra: string[] = []
  headers.forEach((h, i) => {
    const tgt = mapping[h] || TO_NOTES
    const val = (row[i] ?? '').trim()
    if (!val) return
    if (tgt === IGNORE) return
    if (tgt === FIRSTNAME) { first = val; return }
    if (tgt === LASTNAME) { last = val; return }
    if (tgt === TO_NOTES) { notesExtra.push(`${h}: ${val}`); return }
    if (UNIFIED_FIELDS.includes(tgt)) rec[tgt] = rec[tgt] ? `${rec[tgt]} / ${val}` : val
  })
  if (first || last) rec['Full Name'] = `${first} ${last}`.trim()
  // per-import metadata fills in when the sheet didn't supply the column
  if (!rec['Opportunity']) rec['Opportunity'] = meta.opportunity
  if (!rec['Lead Source']) rec['Lead Source'] = meta.leadSource
  if (!rec['Captured Date'] && meta.capturedDate) rec['Captured Date'] = meta.capturedDate
  if (notesExtra.length) rec['Notes'] = (rec['Notes'] ? rec['Notes'] + ' | ' : '') + notesExtra.join(' | ')
  return rec
}

export interface RowIssue {
  level: 'error' | 'warn'
  msg: string
}
const POSTCODE_RE = /^[A-Za-z0-9][A-Za-z0-9 -]{2,9}$/
/** Validate a unified record: required fields, email "@", and obvious column
 * misalignment (email without @, email/postcode in the wrong field). */
export function validateRecord(rec: Record<string, string>): RowIssue[] {
  const issues: RowIssue[] = []
  const email = rec['Email'] || ''
  if (!email) issues.push({ level: 'error', msg: 'Missing Email' })
  else if (!email.includes('@')) issues.push({ level: 'error', msg: 'Email has no “@” — columns may be shifted' })
  if (!rec['Company Name']) issues.push({ level: 'error', msg: 'Missing Company Name' })
  if (rec['Company Name'] && rec['Company Name'].includes('@')) issues.push({ level: 'warn', msg: 'Company Name looks like an email' })
  if (rec['Postcode'] && rec['Postcode'].includes('@')) issues.push({ level: 'warn', msg: 'Postcode looks like an email' })
  if (email && !email.includes('@') && POSTCODE_RE.test(email) && /\d/.test(email)) issues.push({ level: 'warn', msg: 'Email value looks like a postcode' })
  return issues
}

/** Apply a skill-emitted mapping.json ({ mapped, to_notes, combined_full_name }). */
export function applyMappingJson(headers: string[], json: any): Mapping {
  const base = autoMapHeaders(headers)
  const mapped = (json && json.mapped) || {}
  const toNotes: string[] = (json && json.to_notes) || []
  for (const h of headers) {
    if (mapped[h] && UNIFIED_FIELDS.includes(mapped[h])) base[h] = mapped[h]
    else if (toNotes.includes(h)) base[h] = TO_NOTES
  }
  return base
}
